import { ReactElement, useState } from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { FormattedMessage, formatTranslation } from 'intl';
import { useIntl } from 'react-intl';
import { CommonTranslationKeys, DocumentationTranslationKeys } from 'intl/keys';
import { formatMapUnsuitableReason } from 'intl/helpers/documentation';
import { ButtonContained, ButtonText } from 'components/general';
import { formatVersionNumber } from 'shared/domain/documentation/versionState';
import { findMapUnsuitableReason } from 'shared/domain/documentation/mapUnsuitableReason';
import { isPdfExtension } from 'shared/domain/documentation/availableExtensionsAsMap';
import { dialogContentSx, fileTileSx, mapHintInfoSx, mapHintSx, mapHintWarningSx } from './styles';

/** What the user chose about the new version. */
export interface AddVersionChoice {
  name: string;
  makeCurrent: boolean;
}

export interface AddVersionDialogProps {
  isOpen: boolean;
  documentationName: string;
  fileName: string;
  /** The added file's extension, e.g. `pdf`: tells whether it can be the documentation's map. */
  fileExtension?: string;
  versionNumber: number;
  defaultName: string;
  /** The sublocations the documentation is the map of (e.g. `Building A · Ground floor`); missing when it is no map. */
  mapLocation?: string;
  /** While the version is being added the dialog cannot be closed and the choice cannot be confirmed again. */
  loading?: boolean;
  onConfirm: (choice: AddVersionChoice) => void;
  onCancel: () => void;
}

/** Adds a file as a version of a documentation (PT-7316 extends it with uploading a file of its own). */
export function AddVersionDialog({
  isOpen,
  documentationName,
  fileName,
  fileExtension,
  versionNumber,
  defaultName,
  mapLocation,
  loading = false,
  onConfirm,
  onCancel,
}: AddVersionDialogProps): ReactElement {
  const intl = useIntl();
  const [name, setName] = useState(defaultName);
  // The version becomes current unless the user unticks it, each time anew (PT-7117 mockup).
  const [makeCurrent, setMakeCurrent] = useState(true);
  const [wasOpen, setWasOpen] = useState(isOpen);

  // The choice starts anew only when the dialog opens: a default name changing while it is
  // open (e.g. another documentation picked behind it) must not overwrite what the user typed.
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) {
      setName(defaultName);
      setMakeCurrent(true);
    }
  }

  const cancel = loading ? undefined : onCancel;

  return (
    <Dialog open={isOpen} onClose={cancel} fullWidth maxWidth='xs' data-qa='add-version-dialog'>
      <DialogTitle>
        <FormattedMessage id={DocumentationTranslationKeys.addVersionTitle} />
        <Typography variant='body2' color='text.secondary'>
          {documentationName}
        </Typography>
      </DialogTitle>
      <DialogContent sx={dialogContentSx}>
        <Box sx={fileTileSx} data-qa='add-version-file'>
          <UploadFileIcon color='action' />
          <Box minWidth={0}>
            <Typography variant='body2' fontWeight={600} noWrap>
              {fileName}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              <FormattedMessage id={DocumentationTranslationKeys.addVersionFileHint} />
            </Typography>
          </Box>
        </Box>
        <TextField
          size='small'
          disabled
          label={formatTranslation(intl, DocumentationTranslationKeys.addVersionNumberLabel)}
          value={formatVersionNumber(versionNumber) ?? ''}
        />
        <TextField
          size='small'
          autoFocus
          label={formatTranslation(intl, DocumentationTranslationKeys.addVersionNameLabel)}
          value={name}
          onChange={(event) => setName(event.target.value)}
          inputProps={{ 'data-qa': 'add-version-name' }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={makeCurrent}
              onChange={(_, checked) => setMakeCurrent(checked)}
            />
          }
          label={formatTranslation(intl, DocumentationTranslationKeys.addVersionMarkCurrent)}
        />
        {makeCurrent && mapLocation ? (
          <MapHint extension={fileExtension} location={mapLocation} />
        ) : null}
      </DialogContent>
      <DialogActions>
        <ButtonText onClick={cancel} disabled={loading}>
          <FormattedMessage id={CommonTranslationKeys.cancel} />
        </ButtonText>
        <ButtonContained
          onClick={() => onConfirm({ name, makeCurrent })}
          disabled={loading}
          endIcon={loading ? <CircularProgress size={16} color='inherit' /> : undefined}
        >
          <FormattedMessage id={CommonTranslationKeys.add} />
        </ButtonContained>
      </DialogActions>
    </Dialog>
  );
}

/**
 * What becomes of the documentation's map once the new version is current: a
 * warning when the file cannot be a map, a note for a PDF, whose pages are
 * counted only after it is processed.
 */
function MapHint({ extension, location }: { extension: string | undefined; location: string }): ReactElement | null {
  const intl = useIntl();
  const reason = findMapUnsuitableReason({ extension });

  if (reason) {
    return (
      <Box sx={mapHintSx} data-qa='add-version-map-warning'>
        <WarningAmberIcon fontSize='small' sx={mapHintWarningSx} />
        <Typography variant='body2'>
          {formatTranslation(intl, DocumentationTranslationKeys.addVersionMapWarning, {
            location,
            reason: formatMapUnsuitableReason(intl, reason, extension),
          })}
        </Typography>
      </Box>
    );
  }

  if (!isPdfExtension(extension)) {
    return null;
  }

  return (
    <Box sx={mapHintSx} data-qa='add-version-map-info'>
      <InfoOutlinedIcon fontSize='small' sx={mapHintInfoSx} />
      <Typography variant='body2'>
        {formatTranslation(intl, DocumentationTranslationKeys.addVersionMapPdfInfo, { location })}
      </Typography>
    </Box>
  );
}
